import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BuildingOfficeIcon, PlusIcon, PencilIcon, TrashIcon, FolderIcon, ChevronDownIcon, ChevronRightIcon, DocumentTextIcon, XMarkIcon, Squares2X2Icon, ListBulletIcon, MagnifyingGlassIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import type { Departamento, Colaborador } from '../../types';
import { isAdministrator } from '../../utils/companyFilter';
import DepartamentoModal from './DepartamentoModal';
import ConfiguracaoDepartamento from './ConfiguracaoDepartamento';

interface DepartamentosProps {
  initialDepartamentos: Departamento[];
  colaboradores: Colaborador[];
  onDepartamentosChange: (departamentos: Departamento[]) => void;
  session?: { 
    usuario: { role?: string; empresaId?: string; email?: string; };
    empresa?: { id?: string };
  };
  onNavigateToProcedimentos?: (departamentoId: string) => void;
}

const Departamentos: React.FC<DepartamentosProps> = ({ 
  initialDepartamentos,
  colaboradores,
  onDepartamentosChange,
  session,
  onNavigateToProcedimentos
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Salvar parâmetros iniciais da URL IMEDIATAMENTE, antes de serem limpos
  const [parametrosIniciais] = useState(() => ({
    d: searchParams.get('d'),
    p: searchParams.get('p')
  }));
  
  // 🔍 DEBUG: Verificar parâmetros na montagem
  console.log('🔍 [Departamentos] Parâmetros na URL:', {
    d: searchParams.get('d'),
    p: searchParams.get('p'),
    allParams: Object.fromEntries(searchParams.entries()),
    salvos: parametrosIniciais
  });
  
  const [departamentos, setDepartamentos] = useState<Departamento[]>(initialDepartamentos);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDepartamento, setEditingDepartamento] = useState<Departamento | null>(null);
  const [expandedDepartamentos, setExpandedDepartamentos] = useState<Set<string>>(new Set());
  const [procedimentosPorDepartamento, setProcedimentosPorDepartamento] = useState<Record<string, any[]>>({});
  const [modalNovoProcedimento, setModalNovoProcedimento] = useState(false);
  const [departamentoParaNovoProcedimento, setDepartamentoParaNovoProcedimento] = useState<string | null>(null);
  const [novoProcedimento, setNovoProcedimento] = useState({ 
    nome: '', 
    objetivo: '', 
    arquivo: null as File | null,
    colaboradores: [] as string[]
  });
  const [procedimentoVisualizando, setProcedimentoVisualizando] = useState<any | null>(null);
  const [modalVisualizarProcedimento, setModalVisualizarProcedimento] = useState(false);
  const [modalEditarProcedimento, setModalEditarProcedimento] = useState(false);
  const [procedimentoEditando, setProcedimentoEditando] = useState<any | null>(null);
  const [viewMode, setViewMode] = useState<'kanban' | 'lista'>('kanban');
  const [departamentosExpandidos, setDepartamentosExpandidos] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [configurandoDepartamento, setConfigurandoDepartamento] = useState<Departamento | null>(null);
  
  // Toast de feedback
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info'; visible: boolean }>({
    message: '',
    type: 'success',
    visible: false
  });

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 4000);
  };

  // Cores para as pastas de departamentos
  const cores = [
    'bg-blue-500',
    'bg-green-500', 
    'bg-red-500',
    'bg-orange-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-cyan-500'
  ];

  const getCorPasta = (index: number) => {
    return cores[index % cores.length];
  };

  // Função de filtro para busca
  const filtrarPorBusca = (departamento: Departamento, procedimentos: any[]) => {
    if (!searchTerm.trim()) return true;
    
    const termo = searchTerm.toLowerCase().trim();
    
    // Verifica se o nome do departamento contém o termo
    if (departamento.nome.toLowerCase().includes(termo)) return true;
    
    // Verifica se algum procedimento contém o termo
    const temProcedimentoComTermo = procedimentos.some(proc => 
      (proc.nome || proc.titulo || '').toLowerCase().includes(termo) ||
      (proc.objetivo || proc.descricao || '').toLowerCase().includes(termo)
    );
    
    return temProcedimentoComTermo;
  };

  // Filtrar procedimentos pelo termo de busca
  const filtrarProcedimentos = (procedimentos: any[]) => {
    if (!searchTerm.trim()) return procedimentos;
    
    const termo = searchTerm.toLowerCase().trim();
    return procedimentos.filter(proc => 
      (proc.nome || proc.titulo || '').toLowerCase().includes(termo) ||
      (proc.objetivo || proc.descricao || '').toLowerCase().includes(termo)
    );
  };

  useEffect(() => {
    setDepartamentos(initialDepartamentos);
  }, [initialDepartamentos]);

  // Processar parâmetros da URL para abrir departamento/procedimento automaticamente
  useEffect(() => {
    // Usar parâmetros salvos se os atuais estiverem vazios
    const departamentoId = searchParams.get('d') || parametrosIniciais.d;
    const procedimentoId = searchParams.get('p') || parametrosIniciais.p;
    
    // Se há parâmetros (salvos ou na URL) e temos departamentos
    if (departamentoId && departamentos.length > 0) {
      console.log('🔗 Processando parâmetros - Dept:', departamentoId, 'Proc:', procedimentoId);
      console.log('📦 Procedimentos disponíveis:', Object.keys(procedimentosPorDepartamento));
      
      // Expandir o departamento
      setExpandedDepartamentos(new Set([departamentoId]));
      setDepartamentosExpandidos(new Set([departamentoId]));
      
      // Se houver um procedimento específico
      if (procedimentoId) {
        const procedimentos = procedimentosPorDepartamento[departamentoId];
        console.log('📋 Procedimentos do departamento:', procedimentos?.length || 0);
        
        if (procedimentos && procedimentos.length > 0) {
          const procedimento = procedimentos.find(p => {
            const pId = String(p.id || p._id);
            console.log('🔍 Comparando:', pId, 'com', procedimentoId);
            return pId === procedimentoId;
          });
          
          if (procedimento) {
            console.log('✅ Procedimento encontrado! Abrindo modal:', procedimento.nome || procedimento.titulo);
            setProcedimentoVisualizando(procedimento);
            setModalVisualizarProcedimento(true);
            // Limpar os parâmetros após abrir o procedimento
            setSearchParams({});
          } else {
            console.warn('⚠️ Procedimento não encontrado no array');
          }
        } else {
          console.log('⏳ Aguardando procedimentos carregarem...');
        }
      } else {
        // Se não há procedimento específico, apenas limpar parâmetros após expandir departamento
        setSearchParams({});
      }
    }
  }, [departamentos, procedimentosPorDepartamento, searchParams, parametrosIniciais, setSearchParams]);

  // Carregar todos os procedimentos quando o componente monta ou departamentos mudam
  useEffect(() => {
    const carregarTodosProcedimentos = async () => {
      if (departamentos.length === 0) return;
      
      try {
        const empresaId = session?.empresa?.id || session?.usuario?.empresaId || '';
        const response = await fetch(`/api/procedimentos-simples?empresaId=${encodeURIComponent(empresaId)}`);
        if (response.ok) {
          const result = await response.json();
          const todosProcedimentos = result.data || [];
          console.log('📋 Total de procedimentos carregados:', todosProcedimentos.length);
          
          // Agrupar por departamento
          const porDepartamento: Record<string, any[]> = {};
          departamentos.forEach(dept => {
            porDepartamento[dept.id] = [];
          });
          
          todosProcedimentos.forEach((proc: any) => {
            const deptId = proc.departamentoId || proc.departamento;
            if (deptId && porDepartamento[deptId] !== undefined) {
              porDepartamento[deptId].push(proc);
            }
          });
          
          console.log('📋 Procedimentos agrupados:', porDepartamento);
          setProcedimentosPorDepartamento(porDepartamento);
        }
      } catch (error) {
        console.error('Erro ao carregar procedimentos:', error);
      }
    };

    carregarTodosProcedimentos();
  }, [departamentos, session?.empresa?.id, session?.usuario?.empresaId]);

  const handleAddDepartamento = async (newDepartamento: Omit<Departamento, 'id' | 'createdAt' | 'updatedAt'>) => {
    // Use empresa selecionada na sessão como fonte principal do empresaId
    const empresaId = session?.usuario?.empresaId || session?.empresa?.id || '';
    const departamento = {
      ...newDepartamento,
      empresaId,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      // Salvar no backend (sem o campo id manual)
      const userEmail = session?.usuario?.email || '';
      const xUserData = JSON.stringify({
        email: userEmail,
        role: session?.usuario?.role || 'administrator',
        empresaId: empresaId
      });
      const response = await fetch('/api/departamentos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail,
          'X-User-Data': xUserData
        },
        body: JSON.stringify(departamento)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Departamento salvo no backend:', result);
        
        // Recarregar departamentos do backend para sincronizar
        await reloadDepartamentos();
        setModalOpen(false);
      } else {
        const errorData = await response.json();
        console.error('❌ Erro ao salvar departamento no backend:', errorData);
        alert('Erro ao salvar departamento. Tente novamente.');
      }
    } catch (error) {
      console.error('❌ Erro na requisição:', error);
      alert('Erro de conexão. Tente novamente.');
    }
  };

  const reloadDepartamentos = async () => {
    try {
      const empresaId = session?.usuario?.empresaId || '';
      const response = await fetch(`/api/departamentos?empresaId=${encodeURIComponent(empresaId)}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Mapear _id para id para compatibilidade com o frontend
          const departamentosAtualizados = result.data.map((dept: any) => ({
            ...dept,
            id: dept._id || dept.id
          }));
          setDepartamentos(departamentosAtualizados);
          onDepartamentosChange(departamentosAtualizados);
          console.log('✅ Departamentos recarregados do backend:', departamentosAtualizados.length);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao recarregar departamentos:', error);
    }
  };

  const handleEditDepartamento = async (updatedDepartamento: Departamento) => {
    const departamentoAtualizado = { 
      ...updatedDepartamento, 
      updatedAt: new Date(),
  // Preserve empresaId; prefer empresa selecionada se existir
  empresaId: session?.empresa?.id || session?.usuario?.empresaId || updatedDepartamento.empresaId
    };

    // Remover campos que não devem ser atualizados no MongoDB
    const { _id, id, ...dadosParaAtualizar } = departamentoAtualizado as any;

    try {
      // Atualizar no backend usando _id ou id
      const userEmail = session?.usuario?.email || '';
      const departamentoId = (updatedDepartamento as any)._id || updatedDepartamento.id;
      const response = await fetch(`/api/departamentos/${departamentoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail
        },
        body: JSON.stringify(dadosParaAtualizar)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Departamento atualizado no backend:', result);
        
        // Recarregar departamentos do backend para sincronizar
        await reloadDepartamentos();
        setEditingDepartamento(null);
        setModalOpen(false);
      } else {
        const errorData = await response.json();
        console.error('❌ Erro ao atualizar departamento no backend:', errorData);
        alert('Erro ao atualizar departamento. Tente novamente.');
      }
    } catch (error) {
      console.error('❌ Erro na requisição:', error);
      alert('Erro de conexão. Tente novamente.');
    }
  };

  const handleDeleteDepartamento = async (id: string) => {
    const hasDependentEmployees = colaboradores.some(col => col.departamentoId === id);
    
    if (hasDependentEmployees) {
      alert('Não é possível excluir este departamento pois existem colaboradores vinculados a ele. Transfira ou remova os colaboradores primeiro.');
      return;
    }

    if (window.confirm('Tem certeza que deseja excluir este departamento?')) {
      try {
        // Excluir do backend usando _id do MongoDB
        const userEmail = session?.usuario?.email || '';
        const departamento = departamentos.find(dept => dept.id === id);
        const departamentoId = (departamento as any)?._id || id;
        
        const response = await fetch(`/api/departamentos/${departamentoId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': userEmail
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.deleted_count > 0) {
            console.log('✅ Departamento excluído do backend');
            
            // Recarregar departamentos do backend para sincronizar
            await reloadDepartamentos();
          } else {
            console.error('❌ Departamento não encontrado no backend');
            alert('Departamento não encontrado. Pode já ter sido excluído.');
          }
        } else {
          console.error('❌ Erro ao excluir departamento do backend:', response.status);
          alert('Erro ao excluir departamento. Tente novamente.');
        }
      } catch (error) {
        console.error('❌ Erro na requisição:', error);
        alert('Erro de conexão. Tente novamente.');
      }
    }
  };

  // Salvar configurações do departamento
  const handleSaveConfiguracaoDepartamento = async (departamentoAtualizado: Departamento) => {
    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId || '';
      const departamentoId = (departamentoAtualizado as any)._id || departamentoAtualizado.id;
      
      const response = await fetch(`/api/departamentos/${departamentoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            email: session?.usuario?.email || '',
            role: session?.usuario?.role || 'admin',
            empresaId: empresaId
          })
        },
        body: JSON.stringify(departamentoAtualizado)
      });

      if (response.ok) {
        console.log('✅ Configurações do departamento salvas');
        
        // Atualizar localmente
        const updatedDepartamentos = departamentos.map(d => 
          d.id === departamentoAtualizado.id ? departamentoAtualizado : d
        );
        setDepartamentos(updatedDepartamentos);
        onDepartamentosChange(updatedDepartamentos);
        
        // Fechar modal
        setConfigurandoDepartamento(null);
      } else {
        console.error('❌ Erro ao salvar configurações:', response.status);
        alert('Erro ao salvar configurações. Tente novamente.');
      }
    } catch (error) {
      console.error('❌ Erro na requisição:', error);
      alert('Erro de conexão. Tente novamente.');
    }
  };

  const getResponsavelNome = (departamentoId: string, responsavelId: string) => {
    const colaborador = colaboradores.find(col => 
      col.departamentoId === departamentoId && col.id === responsavelId
    );
    return colaborador ? colaborador.nome : 'Não definido';
  };

  const toggleDepartamento = async (departamentoId: string) => {
    const newExpanded = new Set(expandedDepartamentos);
    if (newExpanded.has(departamentoId)) {
      newExpanded.delete(departamentoId);
    } else {
      newExpanded.add(departamentoId);
      // Carregar procedimentos deste departamento se ainda não carregou
      if (!procedimentosPorDepartamento[departamentoId]) {
        await loadProcedimentos(departamentoId);
      }
    }
    setExpandedDepartamentos(newExpanded);
  };

  // Função helper para recarregar todos os procedimentos
  const recarregarTodosProcedimentos = async () => {
    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId || '';
      const response = await fetch(`/api/procedimentos-simples?empresaId=${encodeURIComponent(empresaId)}`);
      if (response.ok) {
        const result = await response.json();
        const todosProcedimentos = result.data || [];
        
        // Agrupar por departamento
        const porDepartamento: Record<string, any[]> = {};
        departamentos.forEach(dept => {
          porDepartamento[dept.id] = [];
        });
        
        todosProcedimentos.forEach((proc: any) => {
          const deptId = proc.departamentoId || proc.departamento;
          if (deptId && porDepartamento[deptId] !== undefined) {
            porDepartamento[deptId].push(proc);
          }
        });
        
        setProcedimentosPorDepartamento(porDepartamento);
        console.log('✅ Procedimentos recarregados:', todosProcedimentos.length);
      }
    } catch (error) {
      console.error('Erro ao recarregar procedimentos:', error);
    }
  };

  const loadProcedimentos = async (departamentoId: string) => {
    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId || '';
      const response = await fetch(`/api/procedimentos-simples?empresaId=${encodeURIComponent(empresaId)}`);
      if (response.ok) {
        const result = await response.json();
        console.log('📋 Procedimentos carregados:', result.data?.length || 0);
        const todosProcedimentos = result.data || [];
        const procedimentos = todosProcedimentos.filter((proc: any) => {
          const procDeptId = proc.departamentoId || proc.departamento;
          console.log(`Comparando: proc.departamentoId="${procDeptId}" com departamentoId="${departamentoId}"`);
          return procDeptId === departamentoId;
        });
        console.log(`✅ Procedimentos filtrados para ${departamentoId}:`, procedimentos.length);
        setProcedimentosPorDepartamento(prev => ({
          ...prev,
          [departamentoId]: procedimentos
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar procedimentos:', error);
    }
  };

  const handleSalvarNovoProcedimento = async () => {
    if (!novoProcedimento.nome.trim() || !novoProcedimento.objetivo.trim() || !departamentoParaNovoProcedimento) return;

    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId || '';
      
      // Upload de arquivo local (não base64)
      let arquivoData = null;
      if (novoProcedimento.arquivo) {
        const formData = new FormData();
        formData.append('file', novoProcedimento.arquivo);
        formData.append('empresaId', empresaId);
        formData.append('departamentoId', departamentoParaNovoProcedimento);
        
        const uploadResponse = await fetch('/api/procedimentos-simples/upload', {
          method: 'POST',
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          arquivoData = {
            nome: uploadResult.originalName,
            url: uploadResult.url,
            tipo: uploadResult.type,
            tamanho: uploadResult.size,
            uploadedAt: uploadResult.uploadedAt
          };
          console.log('✅ Arquivo enviado:', arquivoData);
        } else {
          console.error('❌ Erro no upload do arquivo');
          alert('Erro ao enviar arquivo');
          return;
        }
      }

      const procedimento = {
        nome: novoProcedimento.nome,
        objetivo: novoProcedimento.objetivo,
        arquivo: arquivoData,
        colaboradores: novoProcedimento.colaboradores,
        departamentoId: departamentoParaNovoProcedimento,
        empresaId: empresaId,
        pastaId: null,
        criadoPor: session?.usuario?.id || ''
      };

      const response = await fetch('/api/procedimentos-simples', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(procedimento)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Procedimento criado:', result);
        await loadProcedimentos(departamentoParaNovoProcedimento);
        setModalNovoProcedimento(false);
        setNovoProcedimento({ nome: '', objetivo: '', arquivo: null, colaboradores: [] });
        setDepartamentoParaNovoProcedimento(null);
        showToast(`✅ Procedimento "${novoProcedimento.nome}" criado com sucesso!`, 'success');
      } else {
        console.error('❌ Erro ao criar procedimento:', await response.text());
        showToast('❌ Erro ao criar procedimento', 'error');
      }
    } catch (error) {
      console.error('❌ Erro de conexão:', error);
      showToast('❌ Erro de conexão ao criar procedimento', 'error');
    }
  };

  const handleEditarProcedimento = (proc: any) => {
    setProcedimentoEditando(proc);
    setNovoProcedimento({
      nome: proc.nome,
      objetivo: proc.objetivo,
      arquivo: null,
      colaboradores: proc.colaboradores || []
    });
    setModalVisualizarProcedimento(false);
    setModalEditarProcedimento(true);
  };

  const handleSalvarEdicaoProcedimento = async () => {
    if (!procedimentoEditando || !novoProcedimento.nome.trim() || !novoProcedimento.objetivo.trim()) return;

    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId || '';
      const departamentoId = procedimentoEditando.departamentoId || '';
      
      // Determinar arquivo: novo arquivo, manter atual, ou remover
      let arquivoData = null;
      
      if (novoProcedimento.arquivo) {
        // Novo arquivo selecionado - fazer upload local
        const formData = new FormData();
        formData.append('file', novoProcedimento.arquivo);
        formData.append('empresaId', empresaId);
        formData.append('departamentoId', departamentoId);
        
        const uploadResponse = await fetch('/api/procedimentos-simples/upload', {
          method: 'POST',
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          arquivoData = {
            nome: uploadResult.originalName,
            url: uploadResult.url,
            tipo: uploadResult.type,
            tamanho: uploadResult.size,
            uploadedAt: uploadResult.uploadedAt
          };
          console.log('✅ Novo arquivo enviado:', arquivoData);
        } else {
          console.error('❌ Erro no upload do arquivo');
          alert('Erro ao enviar arquivo');
          return;
        }
      } else if (!procedimentoEditando.arquivoRemovido) {
        // Manter arquivo atual (não foi removido)
        arquivoData = procedimentoEditando.arquivo || null;
      }
      // Se arquivoRemovido = true e não há novo arquivo, arquivoData fica null

      const procedimentoId = procedimentoEditando._id || procedimentoEditando.id;
      
      const procedimentoAtualizado = {
        ...procedimentoEditando,
        nome: novoProcedimento.nome,
        objetivo: novoProcedimento.objetivo,
        arquivo: arquivoData,
        colaboradores: novoProcedimento.colaboradores,
        updatedAt: new Date()
      };
      
      // Remover flag temporária antes de salvar
      delete procedimentoAtualizado.arquivoRemovido;

      const response = await fetch(`/api/procedimentos-simples/${procedimentoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(procedimentoAtualizado)
      });

      if (response.ok) {
        await loadProcedimentos(procedimentoEditando.departamentoId);
        setModalEditarProcedimento(false);
        const nomeProc = novoProcedimento.nome;
        setProcedimentoEditando(null);
        setNovoProcedimento({ nome: '', objetivo: '', arquivo: null, colaboradores: [] });
        showToast(`✅ Procedimento "${nomeProc}" salvo com sucesso!`, 'success');
      } else {
        showToast('❌ Erro ao salvar alterações', 'error');
      }
    } catch (error) {
      console.error('Erro:', error);
      showToast('❌ Erro de conexão', 'error');
    }
  };

  const handleExcluirProcedimento = async (proc: any) => {
    if (!window.confirm(`Tem certeza que deseja excluir o procedimento "${proc.nome}"?`)) {
      return;
    }

    try {
      // Usar _id do MongoDB ou id como fallback
      const procedimentoId = proc._id || proc.id;
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId || '';
      
      console.log('🗑️ Excluindo procedimento:', procedimentoId);
      
      const response = await fetch(`/api/procedimentos-simples/${procedimentoId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            email: session?.usuario?.email || '',
            role: session?.usuario?.role || 'admin',
            empresaId: empresaId
          })
        }
      });

      const result = await response.json();
      console.log('📋 Resultado exclusão:', result);

      if (response.ok && result.success) {
        // Recarregar todos os procedimentos
        const reloadResponse = await fetch(`/api/procedimentos-simples?empresaId=${encodeURIComponent(empresaId)}`);
        if (reloadResponse.ok) {
          const reloadResult = await reloadResponse.json();
          const todosProcedimentos = reloadResult.data || [];
          
          // Reagrupar por departamento
          const porDepartamento: Record<string, any[]> = {};
          departamentos.forEach(dept => {
            porDepartamento[dept.id] = [];
          });
          
          todosProcedimentos.forEach((p: any) => {
            const deptId = p.departamentoId || p.departamento;
            if (deptId && porDepartamento[deptId] !== undefined) {
              porDepartamento[deptId].push(p);
            }
          });
          
          setProcedimentosPorDepartamento(porDepartamento);
        }
        
        setModalVisualizarProcedimento(false);
        setProcedimentoVisualizando(null);
        showToast(`🗑️ Procedimento "${proc.nome}" excluído`, 'info');
      } else {
        console.error('Erro ao excluir:', result);
        showToast(`❌ Erro ao excluir: ${result.error || 'Erro desconhecido'}`, 'error');
      }
    } catch (error) {
      console.error('Erro de conexão:', error);
      showToast('❌ Erro de conexão ao excluir procedimento', 'error');
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 kanban-desktop-scale">
      {/* Header Fixo - Estilo Kanban */}
      <div className="flex-none bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <BuildingOfficeIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Departamentos</h1>
              <p className="text-xs text-gray-500">{departamentos.length} departamento(s)</p>
            </div>
          </div>

          {/* Barra de Pesquisa */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar departamentos ou procedimentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Botões de Visualização */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setViewMode('lista')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'lista'
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Visualização em Lista"
              >
                <ListBulletIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Lista</span>
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'kanban'
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Visualização Kanban"
              >
                <Squares2X2Icon className="h-4 w-4" />
                <span className="hidden sm:inline">Kanban</span>
              </button>
            </div>

            {isAdministrator(session) && (
              <button
                onClick={() => setModalOpen(true)}
                className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Novo Departamento</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Container Principal */}
      <div className={`flex-1 ${viewMode === 'kanban' ? 'overflow-x-auto overflow-y-hidden' : 'overflow-y-auto overflow-x-hidden'} p-4`}>
        {departamentos.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-lg max-w-sm">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <BuildingOfficeIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nenhum departamento
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Crie seu primeiro departamento para organizar os procedimentos.
              </p>
              {isAdministrator(session) && (
                <button
                  onClick={() => setModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  Criar Departamento
                </button>
              )}
            </div>
          </div>
        ) : viewMode === 'lista' ? (
          /* ====================== VISUALIZAÇÃO EM LISTA ====================== */
          <div className="space-y-4 max-w-6xl mx-auto">
            {/* Header da Lista */}
            <div className="bg-white rounded-lg shadow-sm border px-4 py-2.5">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <ListBulletIcon className="h-4 w-4 text-indigo-600" />
                Visualização em Lista
                {searchTerm && (
                  <span className="text-xs text-gray-400 font-normal ml-2">
                    • Filtrando por "{searchTerm}"
                  </span>
                )}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">Departamentos e procedimentos agrupados - clique para expandir</p>
            </div>

            {/* Lista de Departamentos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {departamentos
              .filter(dept => filtrarPorBusca(dept, procedimentosPorDepartamento[dept.id] || []))
              .map((departamento, index) => {
              const procedimentosOriginais = procedimentosPorDepartamento[departamento.id] || [];
              const procedimentos = filtrarProcedimentos(procedimentosOriginais);
              const isExpanded = departamentosExpandidos.has(departamento.id) || (searchTerm.trim() !== '' && procedimentos.length > 0);
              const membrosDepartamento = colaboradores.filter(c => 
                String(c.departamentoId) === String(departamento.id) && c.status === 'ativo'
              );

              const toggleDepartamento = () => {
                const newSet = new Set(departamentosExpandidos);
                if (newSet.has(departamento.id)) {
                  newSet.delete(departamento.id);
                } else {
                  newSet.add(departamento.id);
                }
                setDepartamentosExpandidos(newSet);
              };

              return (
                <div key={departamento.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                  {/* Cabeçalho do Departamento - Clicável */}
                  <div 
                    className={`px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-indigo-50/50 border-b' : ''}`}
                    onClick={toggleDepartamento}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        {/* Seta de expansão */}
                        <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                          <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                        </div>
                        {/* Ícone colorido */}
                        <div className={`w-8 h-8 rounded-lg ${getCorPasta(index)} flex items-center justify-center shadow-sm`}>
                          <FolderIcon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">{departamento.nome}</h3>
                          <p className="text-xs text-gray-500">
                            {procedimentos.length} procedimento{procedimentos.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Avatares dos membros */}
                        {membrosDepartamento.length > 0 && (
                          <div className="flex -space-x-1.5">
                            {membrosDepartamento.slice(0, 3).map((membro, membroIndex) => (
                              <div
                                key={membro.id}
                                className={`w-6 h-6 rounded-full border-2 border-white ${getCorPasta(membroIndex)} flex items-center justify-center text-white text-[9px] font-bold shadow-sm`}
                                title={membro.nome}
                              >
                                {membro.foto ? (
                                  <img src={membro.foto} alt={membro.nome} className="w-full h-full rounded-full object-cover" />
                                ) : (
                                  membro.nome?.charAt(0).toUpperCase()
                                )}
                              </div>
                            ))}
                            {membrosDepartamento.length > 3 && (
                              <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-300 flex items-center justify-center text-gray-600 text-[8px] font-bold">
                                +{membrosDepartamento.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Botões de ação */}
                        {isAdministrator(session) && (
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfigurandoDepartamento(departamento);
                              }}
                              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Configurações do departamento"
                            >
                              <Cog6ToothIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingDepartamento(departamento);
                                setModalOpen(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Editar departamento"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDepartamento(departamento.id);
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Excluir departamento"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Lista de Procedimentos (Expansível) */}
                  {isExpanded && (
                    <div className="p-4 bg-gray-50/50">
                      {procedimentos.length === 0 ? (
                        <div className="text-center py-6">
                          <DocumentTextIcon className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                          <p className="text-sm text-gray-400">Nenhum procedimento neste departamento</p>
                          {isAdministrator(session) && (
                            <button
                              onClick={() => {
                                setDepartamentoParaNovoProcedimento(departamento.id);
                                setModalNovoProcedimento(true);
                              }}
                              className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              + Adicionar Procedimento
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {procedimentos.map((proc: any, procIndex: number) => {
                            const procColaboradores = proc.colaboradores || [];
                            
                            return (
                              <div
                                key={proc._id || proc.id}
                                onClick={() => {
                                  setProcedimentoVisualizando(proc);
                                  setModalVisualizarProcedimento(true);
                                }}
                                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer"
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex items-start gap-3 flex-1">
                                    <div className={`w-9 h-9 rounded-lg ${getCorPasta(procIndex + 10)} flex items-center justify-center shadow-sm flex-shrink-0`}>
                                      <DocumentTextIcon className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-sm font-semibold text-gray-900 hover:text-indigo-700">
                                        {proc.nome || proc.titulo}
                                      </h4>
                                      {(proc.objetivo || proc.descricao) && (
                                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                          {proc.objetivo || proc.descricao}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    {/* Avatares */}
                                    {procColaboradores.length > 0 && (
                                      <div className="flex -space-x-1.5">
                                        {procColaboradores.slice(0, 3).map((colabId: string, avatarIndex: number) => {
                                          const colab = colaboradores.find(c => c.id === colabId);
                                          if (!colab) return null;
                                          return (
                                            <div
                                              key={colabId}
                                              className={`w-6 h-6 rounded-full border-2 border-white ${getCorPasta(avatarIndex + 3)} flex items-center justify-center text-white text-[9px] font-bold shadow-sm`}
                                              title={colab.nome}
                                            >
                                              {colab.nome?.charAt(0).toUpperCase()}
                                            </div>
                                          );
                                        })}
                                        {procColaboradores.length > 3 && (
                                          <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-gray-600 text-[9px] font-bold">
                                            +{procColaboradores.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Indicador de arquivo */}
                                    {proc.arquivo && (
                                      <div className="flex items-center gap-1 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                                        <span>📎</span>
                                        <span>Anexo</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          
                          {/* Botão adicionar */}
                          {isAdministrator(session) && (
                            <button
                              onClick={() => {
                                setDepartamentoParaNovoProcedimento(departamento.id);
                                setModalNovoProcedimento(true);
                              }}
                              className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 text-sm text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg border border-dashed border-gray-300 hover:border-indigo-300 transition-colors"
                            >
                              <PlusIcon className="h-4 w-4" />
                              Adicionar Procedimento
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </div>
        ) : (
          /* ====================== VISUALIZAÇÃO KANBAN ====================== */
          <div className="flex gap-4 min-w-min h-full pb-2">
            {/* Colunas de Departamentos (estilo Kanban) */}
            {departamentos
              .filter(dept => filtrarPorBusca(dept, procedimentosPorDepartamento[dept.id] || []))
              .map((departamento, index) => {
              const procedimentosOriginais = procedimentosPorDepartamento[departamento.id] || [];
              const procedimentos = filtrarProcedimentos(procedimentosOriginais);
              const responsavel = colaboradores.find(c => String(c.id) === String(departamento.responsavel));
              // Filtrar membros do departamento
              const membrosDepartamento = colaboradores.filter(c => 
                String(c.departamentoId) === String(departamento.id) && c.status === 'ativo'
              );
              
              return (
                <div
                  key={departamento.id}
                  className="flex-none w-72 h-full"
                >
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-md border border-white/40 hover:shadow-lg transition-all h-full flex flex-col">
                    {/* Header da Coluna (estilo etapa do Kanban) */}
                    <div className="p-3 border-b border-gray-100">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2.5 min-w-0 flex-1">
                          <div className={`w-9 h-9 rounded-lg ${getCorPasta(index)} flex items-center justify-center shadow-md flex-shrink-0`}>
                            <FolderIcon className="w-5 h-5 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-bold text-gray-900 truncate">
                              {departamento.nome}
                            </h3>
                            <p className="text-[10px] text-gray-500">
                              {procedimentos.length} procedimento(s)
                            </p>
                          </div>
                        </div>
                        
                        {isAdministrator(session) && (
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            <button
                              onClick={() => setConfigurandoDepartamento(departamento)}
                              className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Configurações do departamento"
                            >
                              <Cog6ToothIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingDepartamento(departamento);
                                setModalOpen(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Editar departamento"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteDepartamento(departamento.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Excluir departamento"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Avatares dos membros do departamento (estilo Kanban) */}
                      <div className="flex items-center gap-2 mt-2">
                        {membrosDepartamento.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <div className="flex -space-x-1.5">
                              {membrosDepartamento.slice(0, 5).map((membro, membroIndex) => (
                                <div
                                  key={membro.id}
                                  className={`w-6 h-6 rounded-full border-2 border-white ${getCorPasta(membroIndex + index)} flex items-center justify-center text-white text-[9px] font-bold shadow-sm`}
                                  title={`${membro.nome}${String(membro.id) === String(departamento.responsavel) ? ' (Responsável)' : ''}`}
                                >
                                  {membro.foto ? (
                                    <img src={membro.foto} alt={membro.nome} className="w-full h-full rounded-full object-cover" />
                                  ) : (
                                    membro.nome?.charAt(0).toUpperCase()
                                  )}
                                </div>
                              ))}
                              {membrosDepartamento.length > 5 && (
                                <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-gray-600 text-[9px] font-bold">
                                  +{membrosDepartamento.length - 5}
                                </div>
                              )}
                            </div>
                            <span className="text-[9px] text-gray-500 ml-1">
                              {membrosDepartamento.length} membro{membrosDepartamento.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        ) : responsavel ? (
                          <div className="flex items-center gap-1.5">
                            <div className={`w-6 h-6 rounded-full ${getCorPasta(index + 5)} flex items-center justify-center text-white text-[10px] font-bold shadow-sm`}>
                              {responsavel.nome?.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-[10px] text-gray-600 truncate max-w-[120px]">
                              {responsavel.nome}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400">Sem membros</span>
                        )}
                      </div>
                    </div>

                    {/* Área dos Procedimentos (Cards) - com scroll vertical */}
                    <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
                      {procedimentos.length === 0 ? (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-center py-8 px-4">
                            <div className="w-12 h-12 mx-auto bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                              <DocumentTextIcon className="w-6 h-6 text-gray-400" />
                            </div>
                            <p className="text-xs text-gray-400">Nenhum procedimento</p>
                            {isAdministrator(session) && (
                              <button
                                onClick={() => {
                                  setDepartamentoParaNovoProcedimento(departamento.id);
                                  setModalNovoProcedimento(true);
                                }}
                                className="mt-3 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                              >
                                + Adicionar
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        procedimentos.map((proc: any, procIndex: number) => {
                          const procColaboradores = proc.colaboradores || [];
                          
                          return (
                            <div
                              key={proc.id}
                              onClick={() => {
                                setProcedimentoVisualizando(proc);
                                setModalVisualizarProcedimento(true);
                              }}
                              className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer group"
                            >
                              {/* Título do Procedimento */}
                              <div className="flex items-start gap-2 mb-2">
                                <div className={`w-7 h-7 rounded-lg ${getCorPasta(procIndex + 10)} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                                  <DocumentTextIcon className="w-4 h-4 text-white" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <h4 className="text-xs font-bold text-gray-900 line-clamp-2 group-hover:text-indigo-700 transition-colors">
                                    {proc.nome || proc.titulo}
                                  </h4>
                                </div>
                              </div>
                              
                              {/* Objetivo (descrição) */}
                              {(proc.objetivo || proc.descricao) && (
                                <p className="text-[10px] text-gray-500 line-clamp-2 mb-2 pl-9">
                                  {proc.objetivo || proc.descricao}
                                </p>
                              )}
                              
                              {/* Footer do Card - Avatares e Arquivo */}
                              <div className="flex items-center justify-between pl-9">
                                {/* Avatares dos colaboradores responsáveis */}
                                <div className="flex -space-x-1.5">
                                  {procColaboradores.slice(0, 3).map((colabId: string, avatarIndex: number) => {
                                    const colab = colaboradores.find(c => c.id === colabId);
                                    if (!colab) return null;
                                    return (
                                      <div
                                        key={colabId}
                                        className={`w-5 h-5 rounded-full border-2 border-white ${getCorPasta(avatarIndex + 3)} flex items-center justify-center text-white text-[8px] font-bold shadow-sm`}
                                        title={colab.nome}
                                      >
                                        {colab.nome?.charAt(0).toUpperCase()}
                                      </div>
                                    );
                                  })}
                                  {procColaboradores.length > 3 && (
                                    <div className="w-5 h-5 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-gray-600 text-[8px] font-bold">
                                      +{procColaboradores.length - 3}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Indicador de arquivo anexo */}
                                {proc.arquivo && (
                                  <div className="flex items-center gap-1 text-[9px] text-gray-400">
                                    <span>📎</span>
                                    <span>1 arquivo</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Footer - Botão Adicionar Procedimento */}
                    {isAdministrator(session) && (
                      <div className="p-2 border-t border-gray-100">
                        <button
                          onClick={() => {
                            setDepartamentoParaNovoProcedimento(departamento.id);
                            setModalNovoProcedimento(true);
                          }}
                          className="w-full flex items-center justify-center gap-2 py-2 text-xs text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        >
                          <PlusIcon className="h-4 w-4" />
                          Adicionar Procedimento
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <DepartamentoModal
          departamento={editingDepartamento}
          colaboradores={colaboradores}
          onSave={(departamento) => {
            if (editingDepartamento) {
              handleEditDepartamento(departamento as Departamento);
            } else {
              handleAddDepartamento(departamento as Omit<Departamento, 'id' | 'createdAt' | 'updatedAt'>);
            }
          }}
          onClose={() => {
            setModalOpen(false);
            setEditingDepartamento(null);
          }}
        />
      )}

      {/* Modal Configuração do Departamento */}
      {configurandoDepartamento && (
        <ConfiguracaoDepartamento
          departamento={configurandoDepartamento}
          colaboradores={colaboradores}
          onSave={handleSaveConfiguracaoDepartamento}
          onClose={() => setConfigurandoDepartamento(null)}
          onDelete={(id) => {
            handleDeleteDepartamento(id);
            setConfigurandoDepartamento(null);
          }}
        />
      )}

      {/* Modal Novo Procedimento */}
      {modalNovoProcedimento && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Novo Procedimento</h3>
                <button
                  onClick={() => {
                    setModalNovoProcedimento(false);
                    setNovoProcedimento({ nome: '', objetivo: '', arquivo: null, colaboradores: [] });
                    setDepartamentoParaNovoProcedimento(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Procedimento *
                  </label>
                  <input
                    type="text"
                    value={novoProcedimento.nome}
                    onChange={(e) => setNovoProcedimento(prev => ({ ...prev, nome: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Processo de Compras"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Objetivo *
                  </label>
                  <textarea
                    value={novoProcedimento.objetivo}
                    onChange={(e) => setNovoProcedimento(prev => ({ ...prev, objetivo: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Descreva o objetivo do procedimento..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Documento/Arquivo
                  </label>
                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setNovoProcedimento(prev => ({ ...prev, arquivo: file }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />
                  {novoProcedimento.arquivo && (
                    <p className="text-xs text-gray-500 mt-1">
                      📎 {novoProcedimento.arquivo.name} ({(novoProcedimento.arquivo.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Colaboradores Responsáveis
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                    {colaboradores.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-2">Nenhum colaborador cadastrado</p>
                    ) : (
                      colaboradores.map((colab) => (
                        <label key={colab.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={novoProcedimento.colaboradores.includes(colab.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNovoProcedimento(prev => ({
                                  ...prev,
                                  colaboradores: [...prev.colaboradores, colab.id]
                                }));
                              } else {
                                setNovoProcedimento(prev => ({
                                  ...prev,
                                  colaboradores: prev.colaboradores.filter(id => id !== colab.id)
                                }));
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{colab.nome}</span>
                        </label>
                      ))
                    )}
                  </div>
                  {novoProcedimento.colaboradores.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {novoProcedimento.colaboradores.length} colaborador(es) selecionado(s)
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setModalNovoProcedimento(false);
                      setNovoProcedimento({ nome: '', objetivo: '', arquivo: null, colaboradores: [] });
                      setDepartamentoParaNovoProcedimento(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSalvarNovoProcedimento}
                    disabled={!novoProcedimento.nome.trim() || !novoProcedimento.objetivo.trim()}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Criar Procedimento
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Visualizar Procedimento */}
        {modalVisualizarProcedimento && procedimentoVisualizando && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
              {/* Header Simples */}
              <div className="bg-gray-50 border-b px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <DocumentTextIcon className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">{procedimentoVisualizando.nome}</h3>
                      <p className="text-xs text-gray-500">
                        Criado em {new Date(procedimentoVisualizando.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setModalVisualizarProcedimento(false);
                      setProcedimentoVisualizando(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Objetivo */}
                {procedimentoVisualizando.objetivo && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Objetivo</label>
                    <p className="mt-1 text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                      {procedimentoVisualizando.objetivo}
                    </p>
                  </div>
                )}

                {/* Arquivo */}
                {procedimentoVisualizando.arquivo && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Documento Anexo</label>
                    <div className="mt-1 flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                          <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{procedimentoVisualizando.arquivo.nome}</p>
                          <p className="text-xs text-gray-500">{(procedimentoVisualizando.arquivo.tamanho / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <a
                        href={procedimentoVisualizando.arquivo.url || procedimentoVisualizando.arquivo.dataUrl}
                        download={procedimentoVisualizando.arquivo.nome}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Baixar
                      </a>
                    </div>
                  </div>
                )}

                {/* Colaboradores */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Colaboradores Responsáveis
                    {procedimentoVisualizando.colaboradores?.length > 0 && (
                      <span className="ml-2 text-gray-400">({procedimentoVisualizando.colaboradores.length})</span>
                    )}
                  </label>
                  {procedimentoVisualizando.colaboradores && procedimentoVisualizando.colaboradores.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {procedimentoVisualizando.colaboradores.map((colabId: string) => {
                        const colab = colaboradores.find(c => c.id === colabId);
                        return colab ? (
                          <div key={colabId} className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg">
                            <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                              {colab.nome.charAt(0)}
                            </div>
                            <span className="text-xs text-gray-700">{colab.nome}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400 italic">Nenhum colaborador atribuído</p>
                  )}
                </div>

                {/* Info de datas */}
                <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                  <span>Atualizado em {new Date(procedimentoVisualizando.updatedAt).toLocaleDateString('pt-BR')}</span>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 border-t px-5 py-3 flex items-center justify-between">
                <button
                  onClick={() => {
                    setModalVisualizarProcedimento(false);
                    setProcedimentoVisualizando(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Fechar
                </button>
                {isAdministrator(session) && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleExcluirProcedimento(procedimentoVisualizando)}
                      className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      Excluir
                    </button>
                    <button
                      onClick={() => handleEditarProcedimento(procedimentoVisualizando)}
                      className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Editar
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal Editar Procedimento */}
        {modalEditarProcedimento && procedimentoEditando && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="bg-gray-50 border-b px-5 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-900">Editar Procedimento</h3>
                  <button
                    onClick={() => {
                      setModalEditarProcedimento(false);
                      setProcedimentoEditando(null);
                      setNovoProcedimento({ nome: '', objetivo: '', arquivo: null, colaboradores: [] });
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Conteúdo */}
              <div className="p-5 space-y-4 max-h-[calc(90vh-140px)] overflow-y-auto">
                {/* Nome */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Nome do Procedimento *
                  </label>
                  <input
                    type="text"
                    value={novoProcedimento.nome}
                    onChange={(e) => setNovoProcedimento(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Ex: Política de Segurança"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>

                {/* Objetivo */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Objetivo *
                  </label>
                  <textarea
                    value={novoProcedimento.objetivo}
                    onChange={(e) => setNovoProcedimento(prev => ({ ...prev, objetivo: e.target.value }))}
                    placeholder="Descreva o objetivo deste procedimento..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none"
                    rows={3}
                  />
                </div>

                {/* Arquivo */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Documento/Arquivo
                  </label>
                  
                  {/* Arquivo atual */}
                  {procedimentoEditando.arquivo && !novoProcedimento.arquivo && !procedimentoEditando.arquivoRemovido && (
                    <div className="mb-2 p-2 bg-gray-50 rounded-lg flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-700">{procedimentoEditando.arquivo.nome}</span>
                        <span className="text-xs text-gray-400">(atual)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setProcedimentoEditando(prev => prev ? { ...prev, arquivoRemovido: true, arquivo: null } : null)}
                        className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                      >
                        Remover
                      </button>
                    </div>
                  )}
                  
                  {/* Mensagem de arquivo removido */}
                  {procedimentoEditando.arquivoRemovido && !novoProcedimento.arquivo && (
                    <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between text-sm">
                      <span className="text-yellow-700">Arquivo será removido ao salvar</span>
                      <button
                        type="button"
                        onClick={() => setProcedimentoEditando(prev => prev ? { ...prev, arquivoRemovido: false } : null)}
                        className="text-yellow-600 hover:text-yellow-800 text-xs font-medium underline"
                      >
                        Desfazer
                      </button>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setNovoProcedimento(prev => ({ ...prev, arquivo: file }));
                    }}
                    className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />
                  {novoProcedimento.arquivo && (
                    <p className="text-xs text-green-600 mt-1">
                      Novo: {novoProcedimento.arquivo.name} ({(novoProcedimento.arquivo.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                {/* Colaboradores */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Colaboradores Responsáveis
                    {novoProcedimento.colaboradores.length > 0 && (
                      <span className="ml-2 text-gray-400">({novoProcedimento.colaboradores.length})</span>
                    )}
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {colaboradores.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-2">Nenhum colaborador cadastrado</p>
                    ) : (
                      colaboradores.map((colab) => (
                        <label
                          key={colab.id}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                            novoProcedimento.colaboradores.includes(colab.id)
                              ? 'bg-indigo-50'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={novoProcedimento.colaboradores.includes(colab.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNovoProcedimento(prev => ({
                                  ...prev,
                                  colaboradores: [...prev.colaboradores, colab.id]
                                }));
                              } else {
                                setNovoProcedimento(prev => ({
                                  ...prev,
                                  colaboradores: prev.colaboradores.filter(id => id !== colab.id)
                                }));
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                            {colab.nome?.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm text-gray-700">{colab.nome}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 border-t px-5 py-3 flex items-center justify-end gap-2">
                <button
                  onClick={() => {
                    setModalEditarProcedimento(false);
                    setProcedimentoEditando(null);
                    setNovoProcedimento({ nome: '', objetivo: '', arquivo: null, colaboradores: [] });
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvarEdicaoProcedimento}
                  disabled={!novoProcedimento.nome.trim() || !novoProcedimento.objetivo.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Toast de Notificação */}
      {toast.visible && (
        <div className="fixed bottom-6 right-6 z-[9999] animate-slide-up">
          <div className={`flex items-center gap-3 px-5 py-4 rounded-xl shadow-2xl border backdrop-blur-sm ${
            toast.type === 'success' 
              ? 'bg-green-50/95 border-green-200 text-green-800' 
              : toast.type === 'error'
                ? 'bg-red-50/95 border-red-200 text-red-800'
                : 'bg-blue-50/95 border-blue-200 text-blue-800'
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              toast.type === 'success' 
                ? 'bg-green-100' 
                : toast.type === 'error'
                  ? 'bg-red-100'
                  : 'bg-blue-100'
            }`}>
              {toast.type === 'success' && (
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {toast.type === 'error' && (
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {toast.type === 'info' && (
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            <span className="font-medium text-sm max-w-xs">{toast.message}</span>
            <button 
              onClick={() => setToast(prev => ({ ...prev, visible: false }))}
              className="ml-2 p-1 hover:bg-black/5 rounded-full transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Departamentos;